import { useCallback, useEffect, useState, type SVGProps } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  EmptyState,
  IconInfoCircle,
  Input,
  Page,
  PageHeader,
  Skeleton,
} from '@indipia/ui-core';
import { billingApi } from '../api/billingApi';
import type { PaymentMethod } from '../api/billingApi';
import { TIER_COPY } from '../constants/tierCopy';
import { CANCEL_REASON_ETC_CODE } from '../constants/cancelReasons';
import { ReasonSelectModal } from '../components/ReasonSelectModal';
import { PaymentMethodCardBody, paymentMethodDisplayName } from '../components/PaymentMethodCardBody';

/** BE가 ApiResponse.code로 내려주는 비즈니스 코드(C-03 §4.2) — 구독을 아직 신청한 적 없는
 *  정상 상태(0건)와, 진짜 조회 실패를 구분하기 위해 사용한다(D-07 §17 EmptyState/ErrorState 분리). */
function isSubscriptionNotFound(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('response' in error)) return false;
  const response = (error as { response?: { data?: { code?: string } } }).response;
  return response?.data?.code === 'SUBSCRIPTION_NOT_FOUND';
}

function formatWon(n: number | null | undefined) {
  if (n == null) return '—';
  return n.toLocaleString('ko-KR');
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** ui-core `icons.tsx`에 편집(연필) 아이콘이 없어(D-07 §20 공용 아이콘 목록에 미포함) billing
 *  자체에서 로컬로 그림(PaymentMethodPage.tsx IconPencil과 동일 규약) — 48×48 viewBox,
 *  stroke=currentColor로 맞춰서 나중에 ui-core에 정식 편입되더라도 시각적 차이가 없게 함. */
function IconPencil(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={48}
      height={48}
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M34 6a5.7 5.66 0 1 1 8 8L15 41 4 44l3-11Z" />
    </svg>
  );
}

/** BE는 하이픈 없는 원본값을 그대로 내려줌 — 사업자등록번호 표시 포맷(000-00-00000)은 FE 책임 */
function formatBizNo(bizNo: string | null) {
  if (!bizNo) return '—';
  const digits = bizNo.replace(/\D/g, '');
  if (digits.length !== 10) return bizNo;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

const STATUS_BADGE: Record<string, { label: string; status: 'success' | 'info' | 'warning' | 'mute' }> = {
  ACTIVE: { label: '구독중', status: 'success' },
  TRIAL: { label: '체험중', status: 'info' },
  CANCELED: { label: '해지 예정', status: 'warning' },
  FREE: { label: '무료 이용중', status: 'mute' },
  PAST_DUE: { label: '결제 실패', status: 'warning' },
  GRACE: { label: '유예 기간', status: 'warning' },
};

export function SubscriptionManagePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [cancelPanelOpen, setCancelPanelOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  // useCallback으로 참조 고정 — RefundRequestPage.tsx와 동일 이유(2026-09-17 발견 버그): inline
  // 함수면 이 페이지가 리렌더될 때마다 새 함수가 만들어져 ReasonSelectModal → Modal의 포커스 트랩
  // effect가 재실행되고, 해지 사유 "기타" 입력 중 포커스가 닫기[x]로 튄다.
  const handleCancelModalClose = useCallback(() => setCancelModalOpen(false), []);

  const subscriptionQuery = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: async ({ signal }) => (await billingApi.getSubscription(signal)).data.data,
    // 미구독(SUBSCRIPTION_NOT_FOUND)은 재시도해도 결과가 바뀌지 않는 정상 0건 상태라 재시도 생략
    retry: (failureCount, error) => !isSubscriptionNotFound(error) && failureCount < 2,
  });

  // 진행 중 현장 수. sv-customer 소유 API 직접 호출(구독 조회 성공 여부와 무관한
  // 독립된 실패 축이라 별도 쿼리로 분리). 분모는 플랜 상한(sub.maxConcurrentSite, sv-billing 자체 데이터).
  const siteProgressQuery = useQuery({
    queryKey: ['billing', 'siteProgressCount'],
    queryFn: async ({ signal }) => (await billingApi.getSiteProgressCount(signal)).data.data,
  });

  // 직원 계정 사용량 — 구독 조회 성공 여부와 무관한 독립된 실패 축이라 별도 쿼리로 분리.
  const staffUsageQuery = useQuery({
    queryKey: ['billing', 'staffUsage'],
    queryFn: async ({ signal }) => (await billingApi.getStaffUsage(signal)).data.data,
  });

  // "포함된 에이전트" 섹션의 실제 에이전트 이름 — sv-member 소유(TU_AGENT_SUBSCRIPTION×TM_AGENT).
  // 구독 조회 성공 여부와 무관한 독립된 실패 축이라 별도 쿼리로 분리.
  const subscribedAgentsQuery = useQuery({
    queryKey: ['billing', 'subscribedAgents'],
    queryFn: async ({ signal }) => (await billingApi.getSubscribedAgents(signal)).data.data,
  });

  // 결제 정보 카드의 "결제 수단" 행에 기본 결제수단을 보여주기 위함 — 목록은 기본이 먼저 오도록
  // 정렬돼 응답되므로(billingApi.getPaymentMethods 주석 참고) 첫 항목이 곧 기본 결제수단이다.
  const paymentMethodsQuery = useQuery({
    queryKey: ['billing', 'paymentMethods'],
    queryFn: async ({ signal }) => (await billingApi.getPaymentMethods(signal)).data.data,
  });

  // 세금계산서 자동발행 카드의 상호명·사업자등록번호·대표자명 — sv-member(TU_COMPANY)에서
  // 읽기전용 실데이터로 조회 (원래 결제수단 관리 화면에 있던 섹션을 결제 정보 카드로 이동, 2026-09-02)
  const companyInfoQuery = useQuery({
    queryKey: ['billing', 'companyInfo'],
    queryFn: async ({ signal }) => (await billingApi.getCompanyInfo(signal)).data.data,
  });

  // 해지 패널에서 "7일 이내 결제 건이 있으면 환불로 유도" 안내에 씀(사용자 결정, 2026-09-02) —
  // ACTIVE 상태(해지 가능 상태)일 때만 조회. RefundRequestPage와 동일 쿼리키로 캐시 공유.
  const refundEligiblePaymentsQuery = useQuery({
    queryKey: ['billing', 'refundEligiblePayments'],
    queryFn: async ({ signal }) => (await billingApi.getRefundEligiblePayments(signal)).data.data,
    enabled: subscriptionQuery.data?.subStatus === 'ACTIVE',
  });

  const [receiveEmail, setReceiveEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);

  useEffect(() => {
    // 저장된 값이 있으면 그대로, 없으면 사업자 대표이메일을 기본값으로 안내
    setReceiveEmail(subscriptionQuery.data?.taxReceiveEmail || companyInfoQuery.data?.compEmail || '');
  }, [subscriptionQuery.data?.taxReceiveEmail, companyInfoQuery.data?.compEmail]);

  // 수신 이메일만 "저장" 버튼으로 명시적 저장한다. 자동발행 토글은 2026-10-15 제거 — BE(바로빌 연동)가
  // 사업자번호 있는 계좌이체 결제를 토글과 무관하게 항상 발행하므로(미발급 가산세 방지, 사용자 결정)
  // 끌 수 있는 것처럼 보이면 사실과 어긋난다. autoIssueYn은 API 계약상 필수라 항상 true로 보낸다.
  const updateTaxInvoiceMutation = useMutation({
    mutationFn: (payload: { autoIssueYn: boolean; receiveEmail: string }) =>
      billingApi.updateTaxInvoiceSettings(payload),
    onSuccess: () => {
      toast.success('저장되었습니다');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: () => toast.error('저장에 실패했습니다. 이메일 형식을 확인해주세요.'),
  });

  const cancelMutation = useMutation({
    mutationFn: (payload: { cancelReasonCode: string; cancelReasonText?: string }) =>
      billingApi.cancelSubscription(payload),
    onSuccess: () => {
      // TRIAL 해지는 예약이 아니라 즉시 Free 전환이라(BE cancelSubscription 참고) 문구를 다르게
      // 보여준다 — invalidateQueries 이전이라 sub는 아직 해지 전(TRIAL) 값을 가리킨다.
      toast.success(sub?.subStatus === 'TRIAL' ? '체험이 종료되고 Free로 전환됐어요' : '해지가 예약됐어요');
      setCancelModalOpen(false);
      setCancelPanelOpen(false);
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: () => toast.error('해지 처리에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  const uncancelMutation = useMutation({
    mutationFn: () => billingApi.uncancelSubscription(),
    onSuccess: () => {
      toast.success('해지가 취소됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: () => toast.error('해지 취소에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  // 다운그레이드·결제주기 변경 예약 취소 — 어느 게 예약돼 있는지에 따라 맞는 API를 골라 부른다
  // (둘 다 예약이면 combined, 하나만이면 그에 맞는 단일 취소 API. BE cancelCombinedChangeReservation은
  // nextTierCode·nextBillingCycle 둘 다 초기화하므로 "둘 다 있을 때"만 써야 의도가 명확하다).
  const cancelReservationMutation = useMutation({
    mutationFn: () => {
      if (sub?.nextTierCode && sub?.nextBillingCycle) return billingApi.cancelCombinedChangeReservation();
      if (sub?.nextTierCode) return billingApi.cancelDowngradeReservation();
      return billingApi.cancelCycleChangeReservation();
    },
    onSuccess: () => {
      toast.success('예약이 취소됐어요');
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
    },
    onError: () => toast.error('예약 취소에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  // 결제 재시도는 실패해도 200으로 응답한다(BE 주석 참고) — subStatus로 성공 여부를 직접 판단한다.
  const retryPaymentMutation = useMutation({
    mutationFn: () => billingApi.retryPayment(crypto.randomUUID()),
    onSuccess: (res) => {
      if (res.data.data.subStatus === 'ACTIVE') {
        toast.success('결제가 완료됐어요');
      } else {
        toast.error('아직 결제에 실패하고 있어요. 카드 정보를 다시 확인해주세요.');
      }
      queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    },
    onError: () => toast.error('결제 재시도에 실패했습니다. 잠시 후 다시 시도해주세요.'),
  });

  // 구독 조회가 "진짜 실패"해도 페이지 구조(섹션·버튼·정적 안내)는 그대로 그린다 — 값이 필요한
  // 자리만 "—"/재시도 안내로 대체한다. 통째로 ErrorState로 막지 않는다(사용자 오인 방지).
  // 단, "구독을 아직 신청한 적 없음"(SUBSCRIPTION_NOT_FOUND)은 실패가 아니라 정상 0건 상태라
  // 별도로 분리해 EmptyState로 안내한다(D-07 §17) — free 신청 전까지는 "없는 상태"가 맞다.
  const sub = subscriptionQuery.data;
  const isLoading = subscriptionQuery.isLoading;
  const notSubscribed = subscriptionQuery.isError && isSubscriptionNotFound(subscriptionQuery.error);
  const loadFailed = subscriptionQuery.isError && !notSubscribed;
  const badge = sub ? (STATUS_BADGE[sub.subStatus] ?? { label: sub.subStatus, status: 'mute' as const }) : null;
  const tierCopy = sub ? TIER_COPY[sub.tierCode] : null;
  // 목록이 기본 결제수단부터 오도록 정렬돼 응답되므로 첫 항목이 곧 기본 결제수단이다.
  const defaultPaymentMethod: PaymentMethod | undefined = paymentMethodsQuery.data?.[0];
  const isCanceled = sub?.subStatus === 'CANCELED';
  const isPastDue = sub?.subStatus === 'PAST_DUE';
  const isGrace = sub?.subStatus === 'GRACE';
  // 체험 중엔 NEXT_BILLING_DTM 자체가 없다(BE startTrial — 실제 결제 주기가 시작된 적이 없어서,
  // attemptTrialConversion에서야 비로소 채워짐) — "다음 결제일"의 실질적인 값은 TRIAL_END_DTM이다
  // (2026-09-17 발견·수정 — 예전엔 nextBillingDtm만 봐서 체험 중엔 "다음 결제일"이 빈 값(—)으로
  // 보였음). ACTIVE 등 그 외 상태는 기존처럼 nextBillingDtm을 그대로 쓴다.
  const effectiveNextBillingDtm = sub?.subStatus === 'TRIAL' ? sub?.trialEndDtm : sub?.nextBillingDtm;
  // 해지는 ACTIVE(전이9, 예약 해지)·TRIAL(즉시 Free 전환, 2026-09-16 신설 — 사용자 결정: "해지
  // 버튼은 상태와 무관하게 항상 있어야 하고 체험도 해지가 가능해야 한다")에서 허용한다. PAST_DUE·
  // GRACE 중엔 카드부터 정상화해야 하므로 여전히 제외.
  const canCancel = sub?.subStatus === 'ACTIVE' || sub?.subStatus === 'TRIAL';
  // 해지 패널에서 환불 유도 안내를 보여줄지 — 7일 이내(refundEligible)이면서 서비스 미사용
  // (!alreadyUsed)인 결제 건이 있을 때만 true. 7일 지난 건은 BE(processRefund)가 관리자 승인
  // 큐로는 돌리지만, 이 자기서비스 진입점(환불신청 버튼)에서는 노출하지 않기로 했다(사용자 결정,
  // 2026-09-11 — RefundRequestPage.tsx와 동일 원칙). alreadyUsed도 같은 원칙으로 2026-09-17에
  // 추가(InvoiceHistoryPage.tsx와 동일 규칙 — 예전엔 이용해서 환불이 막힌 건에도 이 버튼이 보였다가
  // RefundRequestPage에서야 막히는 헛걸음이 있었다).
  const hasRefundEligiblePayment =
    refundEligiblePaymentsQuery.data?.some((p) => p.refundEligible && !p.alreadyUsed) ?? false;
  const siteOverLimit =
    sub?.maxConcurrentSite != null &&
    siteProgressQuery.data != null &&
    siteProgressQuery.data.totalElements >= sub.maxConcurrentSite;

  // 다운그레이드·결제주기 변경 예약이 있으면 다음 결제일에 실제로 청구되는 금액은 지금 티어가
  // 아니라 예약된 티어/주기 기준이다(BE SubscriptionLifecycleBatch가 매일 새벽 applyDueDowngrades·
  // applyDueCycleChanges를 청구(chargeRenewals)보다 먼저 실행해 LOCKED_MONTHLY_PRICE를 새 티어
  // 가격으로 먼저 갈아치운 뒤 그 값으로 청구함). sub.monthlyPrice는 "가입/직전 변경 시점 동결가"라
  // 예약 중엔 아직 지금 티어 가격을 가리키므로, 예약이 있을 때만 티어 변경 미리보기 API로 목표
  // 가격을 따로 조회해 화면에 반영한다(2026-09-21 — mobile과 동일하게 수정).
  const hasPendingChange = !!(sub?.nextTierCode || sub?.nextBillingCycle);
  const pendingChangeTargetTier = sub?.nextTierCode ?? sub?.tierCode;
  const pendingChangeTargetCycle = (sub?.nextBillingCycle ?? sub?.billingCycle) ?? undefined;
  const nextTierPreviewQuery = useQuery({
    queryKey: ['billing', 'tierChangePreview', pendingChangeTargetTier, pendingChangeTargetCycle],
    queryFn: async ({ signal }) =>
      (await billingApi.getTierChangePreview(pendingChangeTargetTier!, pendingChangeTargetCycle, signal)).data.data,
    enabled: hasPendingChange && !!pendingChangeTargetTier,
  });
  const nextBillingMonthlyPrice = hasPendingChange
    ? nextTierPreviewQuery.data?.targetMonthlyPrice ?? sub?.monthlyPrice ?? 0
    : sub?.monthlyPrice ?? 0;

  // 다운그레이드·결제주기 변경 예약 안내 문구 — nextTierCode·nextBillingCycle 조합으로 어떤
  // 예약인지 구분한다(reserveDowngrade/reserveCycleChange/reserveCombinedChange 중 하나가 세팅함).
  const pendingReservationLabel = sub?.nextTierCode && sub?.nextBillingCycle
    ? `${TIER_COPY[sub.nextTierCode]?.displayName ?? sub.nextTierCode} 요금제 · ${sub.nextBillingCycle === 'annual' ? '연간' : '월간'} 결제로 변경`
    : sub?.nextTierCode
      ? `${TIER_COPY[sub.nextTierCode]?.displayName ?? sub.nextTierCode} 요금제로 다운그레이드`
      : sub?.nextBillingCycle
        ? `${sub.nextBillingCycle === 'annual' ? '연간' : '월간'} 결제로 변경`
        : null;

  if (notSubscribed) {
    return (
      <Page width="lg">
        <PageHeader title="구독·결제 관리" subtitle="현재 요금제와 결제 정보를 확인하고 관리하세요" />
        <EmptyState
          className="mt-08"
          icon={<IconInfoCircle />}
          title="구독을 시작해보세요"
          description="요금제를 선택하면 1개월 무료체험으로 바로 시작할 수 있어요. 카드 등록은 필요 없어요."
          action={<Button onClick={() => navigate('/agents/billing')}>요금제 보러 가기</Button>}
        />
      </Page>
    );
  }

  return (
    <Page width="lg">
      <PageHeader title="구독·결제 관리" subtitle="현재 요금제와 결제 정보를 확인하고 관리하세요" />

      {loadFailed && (
        <Alert kind="warning" className="mt-06" title="구독 정보를 불러오지 못했습니다">
          새로고침하면 다시 시도합니다. 요금제 변경·해지 등은 정보를 불러온 뒤 이용해주세요.
        </Alert>
      )}

      {isCanceled && (
        <Alert kind="warning" className="mt-06">
          해지가 예약되어 있어요. {formatDate(sub?.cancelReservedDtm)}까지는 계속 이용할 수 있어요.{' '}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => uncancelMutation.mutate()}
            loading={uncancelMutation.isPending}
            disabled={uncancelMutation.isPending}
          >
            해지 취소
          </Button>
        </Alert>
      )}

      {isPastDue && (
        <Alert kind="warning" className="mt-06" title="결제에 실패했어요">
          등록된 카드로 정기결제가 실패했어요.{' '}
          {sub?.nextRetryDtm ? `${formatDate(sub.nextRetryDtm)}에 다시 시도할 예정이에요.` : '곧 다시 시도할 예정이에요.'}{' '}
          카드 정보를 업데이트했다면 기다리지 않고 지금 바로 다시 시도할 수 있어요.
          <div className="mt-03 flex gap-sm">
            <Button variant="secondary" size="sm" onClick={() => navigate('/agents/billing/payment-method')}>
              결제수단 업데이트
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => retryPaymentMutation.mutate()}
              loading={retryPaymentMutation.isPending}
              disabled={retryPaymentMutation.isPending}
            >
              지금 다시 결제하기
            </Button>
          </div>
        </Alert>
      )}

      {isGrace && (
        <Alert kind="warning" className="mt-06" title="유예 기간이에요">
          결제 재시도가 모두 실패해 유예 기간 중이에요.{' '}
          {formatDate(sub?.gracePeriodEndDtm)}까지 결제하지 않으면 Free 요금제로 자동 전환돼요(데이터는 보존돼요).
          <div className="mt-03 flex gap-sm">
            <Button variant="secondary" size="sm" onClick={() => navigate('/agents/billing/payment-method')}>
              결제수단 업데이트
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => retryPaymentMutation.mutate()}
              loading={retryPaymentMutation.isPending}
              disabled={retryPaymentMutation.isPending}
            >
              지금 다시 결제하기
            </Button>
          </div>
        </Alert>
      )}

      {pendingReservationLabel && (
        <Alert kind="info" className="mt-06" title="예약된 변경이 있어요">
          다음 결제일({formatDate(effectiveNextBillingDtm)})부터 {pendingReservationLabel}이(가) 반영돼요.{' '}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => cancelReservationMutation.mutate()}
            loading={cancelReservationMutation.isPending}
            disabled={cancelReservationMutation.isPending}
          >
            예약 취소
          </Button>
        </Alert>
      )}

      {/* 현재 구독 요약 + 결제 메타 + 액션, 한 카드 안에 함께 배치 */}
      <div className="mt-06 rounded-xl bg-group p-06 text-group-onPrimary">
        {isLoading ? (
          <Skeleton height={220} />
        ) : (
          <>
            <div className="flex items-center gap-sm">
              <p className="text-caption font-semibold uppercase tracking-wide opacity-80">현재 이용 중</p>
              {badge && <Badge status={badge.status}>{badge.label}</Badge>}
              {/* 정기 결제(자동갱신) vs 1회성 결제(카드 미등록, 다음 결제일에 자동 FREE 전환) —
                  TRIAL·FREE는 이 구분 자체가 의미 없어 제외(2026-09-10 신설, AUTO_RENEW_YN 매핑). */}
              {sub && sub.tierCode !== 'FREE' && sub.subStatus !== 'TRIAL' && (
                <Badge status={sub.autoRenew ? 'info' : 'mute'}>
                  {sub.autoRenew ? '정기 결제' : '1회성 결제'}
                </Badge>
              )}
            </div>
            <div className="mt-02 flex items-baseline gap-sm">
              <h2 className="text-display-sm font-bold">{tierCopy?.displayName ?? '—'}</h2>
              {sub?.tierCode !== 'FREE' && (
                <p className="text-title-md font-bold">
                  ₩{formatWon(sub?.monthlyPrice)} <span className="text-body-md font-normal opacity-80">/ 월</span>
                </p>
              )}
            </div>
            <p className="mt-03 text-body-sm opacity-90">
              {loadFailed ? '정보를 불러오지 못했어요' : (tierCopy?.description ?? '—')}
            </p>
            {sub?.tierCode === 'FREE' && (
              <p className="mt-02 text-caption opacity-80">별도 만료일 없이 계속 이용할 수 있어요</p>
            )}

            <div className="mt-06 border-t border-group-onPrimary opacity-20" />

            {/* 결제 메타 정보 — FREE는 결제 자체가 없어 전부 자리표시. 1회성 결제(autoRenew=false)와
                해지 예약(isCanceled) 둘 다 "다음 결제"가 실제로는 없는 상태라 같은 문구로 묶는다
                (2026-09-16 — 예전엔 해지 예약 중에도 다음 결제일·결제주기·금액이 마치 갱신될 것처럼
                그대로 노출돼 사용자가 혼란스러워했음. 위 해지 예약 안내 Alert의 "{cancelReservedDtm}
                까지 이용 가능"과 여기 금액·주기가 서로 모순돼 보이던 문제). */}
            <div className="mt-06 grid grid-cols-1 gap-md sm:grid-cols-3">
              <div>
                <p className="text-caption opacity-80">
                  {isCanceled ? '이용 종료일' : sub && sub.tierCode !== 'FREE' && !sub.autoRenew ? '이용 만료일' : '다음 결제일'}
                </p>
                <p className="mt-01 text-body-md font-semibold">
                  {sub?.tierCode === 'FREE'
                    ? '—'
                    : isCanceled
                      ? formatDate(sub?.cancelReservedDtm)
                      : formatDate(effectiveNextBillingDtm)}
                </p>
              </div>
              <div>
                <p className="text-caption opacity-80">결제 주기</p>
                <p className="mt-01 text-body-md font-semibold">
                  {sub?.tierCode === 'FREE'
                    ? '—'
                    : isCanceled
                      ? '해지 예정'
                      : sub
                        ? !sub.autoRenew
                          ? '1회성'
                          : sub.billingCycle === 'annual' ? '연간 결제' : '월간 결제'
                        : '—'}
                </p>
              </div>
              <div>
                <p className="text-caption opacity-80">
                  {isCanceled || (sub && sub.tierCode !== 'FREE' && !sub.autoRenew) ? '다음 결제' : '다음 결제 예정 금액'}
                </p>
                <p className="mt-01 text-body-md font-semibold">
                  {sub?.tierCode === 'FREE'
                    ? '—'
                    : isCanceled || !sub?.autoRenew
                      ? '결제 없음'
                      : `₩${formatWon(nextBillingMonthlyPrice)}`}
                </p>
              </div>
            </div>

            {/* 요금제 변경 · 구독 해지 (구독 조회 실패 시 해지는 판단 불가하므로 숨김) */}
            <div className="mt-06 flex items-center gap-md">
              <Button
                variant="secondary"
                className="border-group-onPrimary text-group-onPrimary"
                onClick={() => navigate('/agents/billing/change-tier')}
              >
                요금제 변경
              </Button>
              {canCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-group-onPrimary"
                  onClick={() => setCancelPanelOpen((v) => !v)}
                >
                  구독 해지
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {cancelPanelOpen && (
        <Alert kind="warning" className="mt-06" title="정말 해지하시겠어요?">
          {sub?.subStatus === 'TRIAL' ? (
            // 체험은 결제한 적이 없어 "주기말까지 이용 보장"할 대상이 없다 — 해지 즉시 Free로
            // 전환된다(BE cancelSubscription, 2026-09-16 신설). ACTIVE 해지 문구와 달리 예약 개념이
            // 없음을 분명히 한다.
            <>지금 해지하면 무료체험이 즉시 종료되고 Free로 전환돼요. 인디포토 현장은 1개까지만 쓸 수 있어요.
              그동안 쌓아둔 데이터는 삭제되지 않고 그대로 남아요.</>
          ) : (
            <>해지해도 이번 결제 주기가 끝나는 {formatDate(sub?.currentPeriodEnd)}까지는 계속 이용할 수 있어요.
              그 이후엔 Free로 자동 전환되고, 인디포토 현장은 1개까지만 쓸 수 있어요. 그동안 쌓아둔 데이터는
              삭제되지 않고 그대로 남아요.</>
          )}
          {hasRefundEligiblePayment && (
            <p className="mt-03 font-semibold">
              결제 후 7일 이내예요 — 지금 환불을 신청하면 결제하신 금액을 전액 돌려받을 수 있어요
              (단, 환불하면 지금 바로 이용이 끊겨요).
            </p>
          )}
          <div className="mt-04 flex flex-wrap gap-sm">
            <Button variant="secondary" size="sm" onClick={() => setCancelPanelOpen(false)}>
              계속 이용할게요
            </Button>
            {hasRefundEligiblePayment && (
              <Button variant="primary" size="sm" onClick={() => navigate('/agents/billing/refund')}>
                환불신청
              </Button>
            )}
            <Button variant="danger" size="sm" onClick={() => setCancelModalOpen(true)}>
              구독 해지
            </Button>
          </div>
        </Alert>
      )}

      {/* 포함된 에이전트 — 개수는 TIER_COPY(FE 정적), 실제 이름은 sv-member(TU_AGENT_SUBSCRIPTION×
          TM_AGENT)에서 조회(2026-09-03 GET /studio/agents 신설). 티어별 정확한 구성 매핑(TM_TIER_AGENT
          시드)은 여전히 미확정이라 개수 자체는 그대로 두고, "지금 실제로 구독 중인" 에이전트 이름만 보여준다. */}
      <Card className="mt-08">
        <h3 className="text-title-sm text-text">포함된 에이전트</h3>
        {isLoading ? (
          <Skeleton height={48} className="mt-03" />
        ) : (
          <>
            {subscribedAgentsQuery.isLoading ? (
              <Skeleton className="mt-03" height={28} />
            ) : subscribedAgentsQuery.data && subscribedAgentsQuery.data.length > 0 ? (
              <div className="mt-03 flex flex-wrap gap-xs">
                {/* Badge의 soft variant 기본 dot(6px, packages/styles 공통 CSS)은 이 목록엔 너무 작아서
                    (사용자 요청) 공통 CSS를 건드리지 않고 여기서만 dot을 키운 커스텀 칩으로 대체 —
                    색상은 Badge soft와 동일한 group-subtle 토큰 사용 */}
                {subscribedAgentsQuery.data.map((agent) => (
                  <span
                    key={agent.agentId}
                    className="inline-flex items-center gap-xs rounded-full bg-group-subtle px-04 py-02 text-caption text-group-onSubtle"
                  >
                    <span className="h-03 w-03 shrink-0 rounded-full bg-group" aria-hidden="true" />
                    {agent.agentName ?? `에이전트 #${agent.agentId}`}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-02 text-caption text-text-muted">구독 중인 에이전트가 없어요.</p>
            )}
          </>
        )}
      </Card>

      {/* 현장 사용량: 진행 중 현장 수(sv-customer 실데이터) / 플랜 상한(sv-billing 실데이터) */}
      <Card className="mt-06">
        <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-title-sm text-text">현장 사용량</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/change-tier')}>
            요금제 변경
          </Button>
        </div>
        <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
          <span className="text-text-muted">진행 중 현장</span>
          <span className="text-text">
            {siteProgressQuery.data?.totalElements ?? '—'} / {sub?.maxConcurrentSiteLabel ?? '—'}
          </span>
        </div>
        {sub?.maxConcurrentSite != null && siteProgressQuery.data && (
          <div className="mt-02 h-02 w-full overflow-hidden rounded-full bg-border">
            <div
              className={`h-full rounded-full ${siteOverLimit ? 'bg-danger' : 'bg-group'}`}
              style={{ width: `${Math.max(0, Math.min(100, (siteProgressQuery.data.totalElements / sub.maxConcurrentSite) * 100))}%` }}
            />
          </div>
        )}
        {siteOverLimit ? (
          <p className="mt-02 text-caption text-danger">진행 현장의 상한을 초과했어요. 요금제 업그레이드를 추천할게요.</p>
        ) : (
          <p className="mt-02 text-caption text-text-muted">
            현장은 {sub?.maxConcurrentSiteLabel ?? '—'}까지 이용이 가능해요. 지금은 여유가 있어요.
          </p>
        )}
      </Card>

      {/* 직원 계정 사용량 — 소속 직원 수(TU_MEMBER_ROLE)·현재 인원 기준 추가요금(TM_STAFF_FEE_TIER).
          "지금 이 순간" 기준 표시일 뿐 청구용 동결가는 아니다(추후작업_TODO.md 참고). */}
      <Card className="mt-06">
        <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-title-sm text-text">직원 계정 사용량</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/staff-fee')}>
            추가요금 자세히 보기
          </Button>
        </div>
        {staffUsageQuery.isLoading ? (
          <Skeleton className="mt-03" height={40} />
        ) : (
          <>
            <div className="mt-03 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
              <span className="text-text-muted">소속 직원</span>
              <span className="text-text">
                {staffUsageQuery.data ? `${staffUsageQuery.data.staffCount}명` : '—'}
              </span>
            </div>
            <div className="mt-02 flex flex-col items-start gap-xs sm:flex-row sm:items-center sm:justify-between text-body-sm">
              <span className="text-text-muted">현재 인원 기준 추가요금</span>
              <span className="text-text">
                {staffUsageQuery.data
                  ? staffUsageQuery.data.surchargeAmount > 0
                    ? `월 ${formatWon(staffUsageQuery.data.surchargeAmount)}원`
                    : '없음'
                  : '—'}
              </span>
            </div>
          </>
        )}
        <p className="mt-02 text-caption text-text-muted">
          기본 제공 인원을 초과하면 인원 구간별로 추가 요금이 붙어요.
        </p>
      </Card>

      {/* 결제 정보 — 전부 실데이터. 세금계산서 자동발행 섹션은 원래 결제수단 관리 화면에 있었으나
          이 카드로 이동(사용자 요청, 2026-09-02). 상호명·사업자등록번호·대표자명은 sv-member
          (TU_COMPANY)에서 읽기전용 실데이터로 조회. 수신 이메일은 TU_SUBSCRIPTION에 저장(사용자 결정,
          2026-08-31). 자동발행 토글은 2026-10-15 제거 — 계좌이체 결제는 항상 바로빌로 발행된다. */}
      <Card className="mt-06">
        <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-title-sm text-text">결제 정보</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/invoices')}>
            결제내역 보기
          </Button>
        </div>
        <ul className="mt-04 divide-y divide-border">
          <li className="flex flex-col items-start gap-xs py-04 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between text-body-sm">
            <div>
              <span className="text-text-muted">결제 수단</span>
              {paymentMethodsQuery.isLoading ? (
                <Skeleton className="mt-01" height={20} width={140} />
              ) : (
                defaultPaymentMethod && (
                  <div className="mt-01">
                    <p className="text-body-sm font-semibold text-text">
                      {paymentMethodDisplayName(defaultPaymentMethod)}
                    </p>
                    <PaymentMethodCardBody method={defaultPaymentMethod} compact />
                  </div>
                )
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/agents/billing/payment-method')}>
              {defaultPaymentMethod ? '변경' : '등록하기'}
            </Button>
          </li>
          <li className="flex flex-col items-start gap-xs py-04 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between text-body-sm">
            <div>
              <span className="text-text-muted">다음 결제</span>
              <p className="mt-01 text-body-sm font-semibold text-text">
                {sub?.tierCode === 'FREE'
                  ? '해당 없음'
                  : isCanceled
                    ? `${formatDate(sub?.cancelReservedDtm)} 이용 종료 (해지 예약, 결제 없음)`
                    : !sub?.autoRenew
                      ? `${formatDate(sub?.nextBillingDtm)} 이용 만료 (1회성 결제, 자동 갱신 없음)`
                      : `${formatDate(effectiveNextBillingDtm)} · ₩${formatWon(nextBillingMonthlyPrice)}`}
              </p>
            </div>
          </li>
        </ul>

        <Divider className="mt-02" />

        <div className="mt-04">
          <span className="text-body-sm text-text-muted">세금계산서</span>

          <div className="mt-05 grid grid-cols-1 gap-md sm:grid-cols-2">
            <Input width="fill" label="상호명" value={companyInfoQuery.data?.compName ?? '—'} readOnly disabled />
            <Input width="fill" label="사업자등록번호" value={formatBizNo(companyInfoQuery.data?.compNo ?? null)} readOnly disabled />
            <Input width="fill" label="대표자명" value={companyInfoQuery.data?.compRepName ?? '—'} readOnly disabled />
            <div className="flex items-end gap-sm">
              <Input
                width="fill"
                label="계산서 수신 이메일"
                placeholder="세금계산서를 받을 이메일"
                value={receiveEmail}
                onChange={(e) => setReceiveEmail(e.target.value)}
                readOnly={!editingEmail}
                disabled={!editingEmail}
              />
              {/* 고정 폭 슬롯 — 연필 아이콘 버튼과 "저장" 버튼의 폭 차이로 옆 Input(width="fill")이
                  상태 전환마다 넓어졌다 좁아졌다 하지 않도록 액션 자리 자체를 고정폭으로 둔다. */}
              <div className="flex w-20 shrink-0 justify-center">
                {editingEmail ? (
                  <Button
                    variant="primary"
                    className="w-full"
                    loading={updateTaxInvoiceMutation.isPending}
                    disabled={updateTaxInvoiceMutation.isPending}
                    onClick={() =>
                      updateTaxInvoiceMutation.mutate(
                        { autoIssueYn: true, receiveEmail },
                        { onSuccess: () => setEditingEmail(false) },
                      )
                    }
                  >
                    저장
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-0"
                    onClick={() => setEditingEmail(true)}
                    aria-label="계산서 수신 이메일 수정"
                  >
                    <IconPencil width={16} height={16} />
                  </Button>
                )}
              </div>
            </div>
          </div>

          <p className="mt-02 text-caption text-text-muted">
            계좌이체로 결제하면 등록된 사업자 정보로 세금계산서가 자동 발행돼요.
            <br />
            카드 결제는 카드 매출전표(영수증)로 증빙돼요.
          </p>
        </div>
      </Card>

      <ReasonSelectModal
        open={cancelModalOpen}
        submitting={cancelMutation.isPending}
        onClose={handleCancelModalClose}
        onConfirm={(cancelReasonCode, cancelReasonText) =>
          cancelMutation.mutate({ cancelReasonCode, cancelReasonText })
        }
        title="해지 사유를 알려주세요"
        selectLabel="해지 사유"
        etcCode={CANCEL_REASON_ETC_CODE}
        confirmLabel="해지 확정"
        confirmVariant="danger"
        queryKey={['billing', 'cancelReasons']}
        fetchReasons={async (signal) => (await billingApi.getCancelReasons(signal)).data.data}
      />
    </Page>
  );
}
